/**
 * ArmadaApi — a small reusable client for the Atomic ecom middleware.
 *
 * Config is injected from Liquid via `window.armadaApi` (see snippets/js-variables.liquid):
 *   { baseUrl: '/apps/armada', locale: 'cs-cz' }
 *
 * Usage:
 *   const res = await ArmadaApi.post('/orders/validate-return', { orderId, email });
 *   if (res.ok) { ...res.data } else { ...res.error.message }
 *
 * `locale` is injected into the request body automatically (override by passing
 * `locale` in the body, or opt out with options.includeLocale = false).
 *
 * Calls NEVER throw — every call resolves to a normalized result so callers
 * have one shape to handle:
 *   {
 *     ok: boolean,
 *     status: number,            // HTTP status, or 0 for client-side failures
 *     data: any | null,          // parsed JSON body, when present
 *     error: { type, message } | null
 *   }
 *   error.type ∈ 'config' | 'network' | 'timeout' | 'http' | 'parse'
 */
(function () {
  const config = window.armadaApi || {};
  const DEFAULT_TIMEOUT = 15000;

  function result(ok, status, data, errorType, errorMessage) {
    return {
      ok,
      status,
      data,
      error: ok ? null : { type: errorType, message: errorMessage },
    };
  }

  function buildUrl(path) {
    // An absolute URL (e.g. the ReverseLogix encoder on another host) is used
    // as-is; a path is appended to the configured base.
    if (/^https?:\/\//i.test(path)) return String(path);
    const base = (config.baseUrl || '').replace(/\/+$/, '');
    const suffix = String(path).startsWith('/') ? path : `/${path}`;
    return `${base}${suffix}`;
  }

  async function request(method, path, body, options = {}) {
    const timeout = options.timeout != null ? options.timeout : DEFAULT_TIMEOUT;

    const isAbsolute = /^https?:\/\//i.test(String(path));
    if (!isAbsolute && !config.baseUrl) {
      return result(false, 0, null, 'config', 'Armada API base URL is not configured.');
    }

    // Inject locale into the payload unless the caller opts out or sets it explicitly.
    let payload = body;
    if (
      payload &&
      typeof payload === 'object' &&
      options.includeLocale !== false &&
      config.locale &&
      payload.locale == null
    ) {
      payload = { ...payload, locale: config.locale };
    }

    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);

    let response;
    try {
      response = await fetch(buildUrl(path), {
        method,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(options.headers || {}),
        },
        body: payload != null ? JSON.stringify(payload) : undefined,
        signal: controller.signal,
      });
    } catch (err) {
      clearTimeout(timer);
      const isTimeout = err && err.name === 'AbortError';
      return result(
        false,
        0,
        null,
        isTimeout ? 'timeout' : 'network',
        isTimeout ? 'The request timed out. Please try again.' : 'A network error occurred. Please try again.'
      );
    }
    clearTimeout(timer);

    // Parse the body defensively — tolerate empty responses, fail clearly on bad JSON.
    let data = null;
    const text = await response.text().catch(() => '');
    if (text) {
      try {
        data = JSON.parse(text);
      } catch (_err) {
        if (response.ok) {
          return result(false, response.status, null, 'parse', 'The server returned an unexpected response.');
        }
      }
    }

    if (!response.ok) {
      const message =
        (data && (data.message || data.error || data.detail)) || `Request failed (${response.status}).`;
      return result(false, response.status, data, 'http', message);
    }

    return result(true, response.status, data, null, null);
  }

  window.ArmadaApi = {
    request,
    get: (path, options) => request('GET', path, null, options),
    post: (path, body, options) => request('POST', path, body, options),
    put: (path, body, options) => request('PUT', path, body, options),
    delete: (path, options) => request('DELETE', path, null, options),
  };
})();
